import { useRef, useState } from 'react';
import { Box, Typography, Button, Paper, List, ListItem, Checkbox, ListItemText, Stack, TextField, ListItemSecondaryAction, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CloudUpload from '@mui/icons-material/CloudUpload';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { useFoodHistory } from '../context/FoodHistoryContext';
import { FoodCamera } from '../components/FoodCamera';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAnalysisPending } from '../context/AnalysisPendingContext';
import { useHistoryRefresh } from '../context/HistoryRefreshContext';
import FeedbackModal from '../components/FeedbackModal';
import ImageAnalysisModal from '../components/ImageAnalysisModal';

// Tipo para alimentos con opciones
interface AlimentoOpcion {
  opciones: string[];
  seleccionado: string;
  aceptado?: boolean;
}

// Tipo para las preguntas de la IA
interface Pregunta {
  pregunta: string;
  opciones: string[];
  resuelta?: string; // Para guardar la opción seleccionada por el usuario
}

// Interfaz para la información nutricional
interface NutritionInfo {
  calorias: number;
  proteinas: number;
  carbohidratos: number;
  grasas: number;
  fibra: number;
}

// Helper para convertir dataURL (base64) a un objeto File
function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
        throw new Error("Invalid data URL");
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while(n--){
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type:mime});
}

// Helper para capitalizar la primera letra de un string
const capitalizeFirstLetter = (string: string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export default function Register() {
  const { addEntry } = useFoodHistory();
  const { user } = useAuth(); // Obtener el usuario del contexto de autenticación
  const [image, setImage] = useState<string | null>(null);
  const [detected, setDetected] = useState<string[]>([]);
  const [preguntas, setPreguntas] = useState<Pregunta[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loadingImageAnalysis, setLoadingImageAnalysis] = useState(false);
  const [missing, setMissing] = useState<'si' | 'no' | null>(null);
  const [missingText, setMissingText] = useState('');
  const [respuestaIA, setRespuestaIA] = useState<string>('');
  const [showAnalysis, setShowAnalysis] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [jsonIA, setJsonIA] = useState<any>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [nutritionInfo, setNutritionInfo] = useState<NutritionInfo | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState('');
  const navigate = useNavigate();
  const { setAnalysisPending } = useAnalysisPending();
  const { setHistoryNeedsRefresh } = useHistoryRefresh();
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [loadingAddToDay, setLoadingAddToDay] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisModalData, setAnalysisModalData] = useState<any>(null);
  const [showInitialAnalysisModal, setShowInitialAnalysisModal] = useState(false);
  const [initialAnalysisData, setInitialAnalysisData] = useState<any>(null);

  // Detectar si es móvil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Analizar imagen con OpenAI (versión refactorizada)
  const analyzeWithOpenAI = async (imageUrl: string, imageId: string) => {
    setLoadingImageAnalysis(true);
    setShowInitialAnalysisModal(true);
    try {
      const result = await api.analyzeImage(imageUrl, imageId, threadId);
      if (result) {
        setInitialAnalysisData({
          imageUrl: imageUrl,
          analysis: result.analysis?.analisis || result.analysis?.analysis || '',
          nutrition: result.nutricion,
          alimentos: result.alimentos || [],
          preguntas: result.preguntas || [],
        });
        setNutritionInfo(result.nutricion);
        setRespuestaIA(result.analysis?.analisis || result.analysis?.analysis || '');
        setDetected(result.alimentos || []);
        setSelected(result.alimentos || []);
        setPreguntas(result.preguntas || []);
      }
      setJsonIA(result.analysis);
      if (result.threadId) setThreadId(result.threadId);
    } catch (error: any) {
      alert(`Error al analizar la imagen. Intenta de nuevo.\n${error.message || ''}`);
      setDetected([]);
      setPreguntas([]);
      setSelected([]);
      setRespuestaIA('');
      setJsonIA(null);
    } finally {
      setLoadingImageAnalysis(false);
      setShowInitialAnalysisModal(false);
    }
  };

  const handleQuestionAnswer = (preguntaIndex: number, opcion: string) => {
    // 1. Añadir la opción seleccionada a la lista de alimentos detectados y a los seleccionados
    setDetected(prev => [...prev, opcion]);
    setSelected(prev => [...prev, opcion]);

    // 2. Marcar la pregunta como resuelta (o eliminarla)
    // En este caso, la eliminamos de la lista para que desaparezca de la UI
    setPreguntas(prev => prev.filter((_, index) => index !== preguntaIndex));
  };

  // Manejar la selección de imagen desde un input de archivo
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(URL.createObjectURL(file)); // Previsualización inmediata
      
      let uploadResult;
      try {
        setLoadingImageAnalysis(true);
        // 1. Subir imagen (única operación dentro de este try/catch)
        uploadResult = await api.uploadImage(file);
        setImage(uploadResult.url);
        setImageId(uploadResult.id);
      } catch (error) {
        console.error('Error en el flujo de subida de imagen:', error);
        alert('Error al subir la imagen.');
        setImage(null);
        setImageId(null);
        setLoadingImageAnalysis(false);
        return; // Detener la ejecución si la subida falla
      } finally {
        setLoadingImageAnalysis(false);
      }
      
      // 2. Lanzar el análisis (fuera del try/catch de la subida)
      // La propia función analyzeWithOpenAI ya maneja sus errores.
      await analyzeWithOpenAI(uploadResult.url, uploadResult.id);
    }
  };

  // Manejar la foto capturada por el componente FoodCamera
  const handleCapture = async (dataUri: string) => {
    setShowCamera(false);
    setImage(dataUri); // Previsualización inmediata

    let uploadResult;
    try {
      setLoadingImageAnalysis(true);
      // 1. Convertir base64 a File y subir imagen
      const file = dataURLtoFile(dataUri, `capture-${Date.now()}.jpg`);
      uploadResult = await api.uploadImage(file);
      setImage(uploadResult.url);
      setImageId(uploadResult.id);
    } catch (error) {
      console.error("Error en flujo de captura y subida:", error);
      alert("Error al procesar la foto capturada.");
      setImage(null);
      setImageId(null);
      setLoadingImageAnalysis(false);
      return; // Detener la ejecución si la subida falla
    } finally {
      setLoadingImageAnalysis(false);
    }

    // 2. Lanzar el análisis (fuera del try/catch de la subida)
    await analyzeWithOpenAI(uploadResult.url, uploadResult.id);
  };

  const handleToggle = (veg: string) => {
    setSelected((prev) =>
      prev.includes(veg) ? prev.filter((v) => v !== veg) : [...prev, veg]
    );
  };

  const handleAddToDay = async () => {
    setLoadingAddToDay(true);
    if (!imageId) {
      console.error('❌ No hay imageId disponible');
      alert('Error: No se pudo procesar la imagen correctamente. Intenta tomar la foto de nuevo.');
      setLoadingAddToDay(false);
      return;
    }
    try {
      // Guardar la entrada en la BBDD (backend)
      const saveResult = await api.post('/add-food-entry', {
        user_id: user?.id,
        image_id: imageId,
        foods: selected,
        nutrition_info: nutritionInfo
          ? {
              calorias: nutritionInfo.calorias,
              proteinas: nutritionInfo.proteinas,
              carbohidratos: nutritionInfo.carbohidratos,
              grasas: nutritionInfo.grasas,
              fibra: nutritionInfo.fibra,
            }
          : { calorias: 0, proteinas: 0, carbohidratos: 0, grasas: 0, fibra: 0 },
        date: new Date().toISOString(),
        ia_json: jsonIA
      });
      if (!saveResult || !saveResult.ok) {
        console.error('❌ Error al guardar en la BBDD:', saveResult);
        setErrorModalMessage('No se pudo guardar la entrada en la base de datos. Intenta de nuevo.');
        setErrorModalOpen(true);
        setLoadingAddToDay(false);
        return;
      }
      // Guardar en el estado local solo si el backend responde ok
      await addEntry({
        foods: selected,
        imageUrl: image || '',
        nutritionInfo: nutritionInfo
          ? {
              calories: nutritionInfo.calorias,
              protein: nutritionInfo.proteinas,
              carbs: nutritionInfo.carbohidratos,
              fiber: nutritionInfo.fibra,
            }
          : { calories: 0, protein: 0, carbs: 0, fiber: 0 },
        date: new Date().toISOString(),
        iaJson: jsonIA
      });
      setSuccessModalOpen(true);
      setSuccessModalMessage('¡Has agregado a tu día!');
      setHistoryNeedsRefresh(true); // Marcar historial para refresco
    } catch (error) {
      setErrorModalMessage('Error al guardar la entrada. Intenta de nuevo.');
      setErrorModalOpen(true);
    } finally {
      setLoadingAddToDay(false);
    }
  };

  const handleReset = () => {
    setImage(null);
    setDetected([]);
    setPreguntas([]);
    setSelected([]);
    setMissing(null);
    setMissingText('');
    setImageId(null);
    setShowCamera(false);
    setJsonIA(null);
    setNutritionInfo(null);
    // No reseteamos el threadId aquí para que persista en la sesión
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    handleReset();
    setAnalysisPending(true);
    navigate('/');
  };

  // Función para comparar arrays de ingredientes
  function arraysIguales(arr1: string[], arr2: string[]) {
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1.map(i => i.toLowerCase().trim()));
    const set2 = new Set(arr2.map(i => i.toLowerCase().trim()));
    for (let item of set1) if (!set2.has(item)) return false;
    return true;
  }

  const handleValidateAndShow = async () => {
    if (!image) return;
    setLoadingValidation(true);
    try {
      const result = await api.validatePlate(image, selected);
      setValidationResult(result);
      setShowValidation(true);
      // Actualizar nutrición y análisis con la respuesta del asistente
      setNutritionInfo(result.nutricion);
      setRespuestaIA(result.analisis);
    } catch (error: any) {
      setErrorModalMessage(error.message || 'Error al validar el plato');
      setErrorModalOpen(true);
    } finally {
      setLoadingValidation(false);
    }
  };

  const handleAddToDayWithValidation = async () => {
    // Si la lista seleccionada es diferente a la detectada o el usuario responde 'no', validar primero
    if (!arraysIguales(detected, selected) || missing === 'no') {
      await handleValidateAndShow();
      return;
    }
    // Si ya está validado y el usuario confirma, guardar
    await handleAddToDay();
  };

  const handleReanalyze = async () => {
    setLoadingValidation(true);
    setShowAnalysisModal(true);
    try {
      let ingredientesParaIA = [...selected];
      if (missing === 'si' && missingText.trim()) {
        const manual = missingText.trim();
        if (!ingredientesParaIA.map(i => i.toLowerCase()).includes(manual.toLowerCase())) {
          ingredientesParaIA = [...ingredientesParaIA, manual];
        }
      }
      console.log('Ingredientes enviados a la IA para reanálisis:', ingredientesParaIA);
      const result = await api.validatePlate(image!, ingredientesParaIA);
      setAnalysisModalData(result);
      setNutritionInfo(result.nutricion);
      setRespuestaIA(result.analisis);
    } catch (error: any) {
      setErrorModalMessage('Error al analizar el plato. Intenta de nuevo.');
      setErrorModalOpen(true);
    } finally {
      setLoadingValidation(false);
    }
  };

  // Al aceptar el análisis inicial, cerrar el modal y actualizar ingredientes
  const handleAcceptInitialAnalysis = () => {
    if (initialAnalysisData) {
      setDetected(initialAnalysisData.alimentos || []);
      setSelected(initialAnalysisData.alimentos || []);
      setNutritionInfo(initialAnalysisData.nutrition);
      setRespuestaIA(initialAnalysisData.analysis);
    }
    setShowInitialAnalysisModal(false);
  };

  // Al aceptar el análisis de reanálisis, actualizar ingredientes y nutrición exactamente con lo que devuelve la IA
  const handleAcceptReanalysis = () => {
    if (analysisModalData) {
      setDetected(analysisModalData.ingredientes_finales || []);
      setSelected(analysisModalData.ingredientes_finales || []);
      setNutritionInfo(analysisModalData.nutricion);
      setRespuestaIA(analysisModalData.analisis);
      setMissing(null);
      setMissingText('');
    }
    setShowAnalysisModal(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4faef', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="h5" fontWeight={700} color="primary" mb={3} align="center">
        Conéctate a nuestra IA
      </Typography>

      {/* Ilustración central o imagen */}
      {!image ? (
        <Box sx={{ position: 'relative', mb: 3 }}>
          <Box
            sx={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              border: '3px solid',
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#e8f5e9',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <RestaurantMenuIcon sx={{ fontSize: 70, color: 'primary.main' }} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              left: -12,
              width: 164,
              height: 164,
              border: '3px dashed',
              borderColor: 'primary.main',
              borderRadius: 4,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
        </Box>
      ) : (
        <Box sx={{ position: 'relative', mb: 3, cursor: 'pointer' }} onClick={() => setShowFullImage(true)}>
          <img
            src={image}
            alt="Plato subido"
            style={{ width: 220, height: 220, objectFit: 'cover', borderRadius: '16px', border: '4px solid #388e3c', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
          />
          <Box sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '50%', p: 0.5 }}>
            <span role="img" aria-label="ampliar">🔍</span>
          </Box>
        </Box>
      )}

      {/* Botones de acción */}
      {!image && (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 2,
            width: '100%',
            maxWidth: '600px',
            bgcolor: '#ffffff',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <RestaurantMenuIcon color="primary" sx={{ fontSize: 40 }}/>
          <Typography variant="h6" align="center">Añade tus vegetales</Typography>
          <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 2 }}>
            Sube o toma una foto de tus alimentos y nuestra IA los analizará por ti.
          </Typography>
          <Stack direction="column" spacing={2} sx={{ width: '100%', maxWidth: '300px' }}>
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
                fullWidth
          >
              Subir Imagen
          <input
            type="file"
            accept="image/*"
            hidden
                onChange={handleImageChange}
                ref={fileInputRef}
          />
            </Button>
            <Button
              variant="contained"
              startIcon={<PhotoCamera />}
              onClick={() => setShowCamera(true)}
              fullWidth
            >
              Hacer Foto
            </Button>
        </Stack>
        </Paper>
      )}

      {showCamera && (
        <FoodCamera
          onCapture={handleCapture}
          onCancel={() => setShowCamera(false)}
        />
      )}

      {/* Resultado del análisis */}
      {image && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, width: '100%', maxWidth: 350, borderRadius: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Tu plato contiene:
          </Typography>
          {loadingImageAnalysis ? (
            <ImageAnalysisModal open={loadingImageAnalysis && !!image} />
          ) : (
            <List>
              {detected.map((item: string, idx) => (
                <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                    <Checkbox
                      edge="start"
                    checked={selected.includes(item)}
                    onChange={() => handleToggle(item)}
                        sx={{ color: 'primary.main', p: 0, mr: 1 }}
                      />
                  <ListItemText
                    primary={<Typography variant="body1">{capitalizeFirstLetter(item)}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Nueva sección para las preguntas de la IA */}
          {!loadingImageAnalysis && preguntas.length > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="grey.300">
              {preguntas.map((p, index) => (
                <Box key={index} mb={2}>
                  <Typography fontWeight={500} mb={1}>{p.pregunta}</Typography>
                  <Stack direction="row" spacing={1}>
                    {p.opciones.map(opcion => (
              <Button
                        key={opcion}
                        variant="outlined"
                        size="small"
                        onClick={() => handleQuestionAnswer(index, opcion)}
              >
                        {capitalizeFirstLetter(opcion)}
              </Button>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          )}

          {/* ¿Crees que ha faltado algo? */}
          {!loadingImageAnalysis && detected.length > 0 && (
            <Box>
              <Box mt={2} pt={2} borderTop={1} borderColor="grey.300">
                <Typography fontWeight={500} mb={1}>¿Crees que ha faltado algo?</Typography>
                <Stack direction="row" spacing={2} mb={1}>
                  <Button
                    variant={missing === 'si' ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => setMissing('si')}
                  >
                    Sí
                  </Button>
                  <Button
                    variant={missing === 'no' ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => setMissing('no')}
                  >
                    No
                  </Button>
                </Stack>
                {missing === 'si' && (
                  <TextField
                    fullWidth
                    label="¿Qué ha faltado?"
                    value={missingText}
                    onChange={e => setMissingText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                )}
                {(missing === 'si' || !arraysIguales(detected, selected)) && (
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    sx={{ mt: 2, mb: 1, fontWeight: 700, borderRadius: 3 }}
                    onClick={handleReanalyze}
                    disabled={loadingValidation}
                  >
                    {loadingValidation ? 'Analizando...' : 'Analizar nuevamente'}
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Sección de Información Nutricional */}
          {!loadingImageAnalysis && nutritionInfo && (
            <Box mt={2} pt={2} borderTop={1} borderColor="grey.300">
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Información Nutricional (aprox.)
              </Typography>
              <List dense sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2 }}>
                <ListItem>
                  <ListItemText primary="Calorías" />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" color="text.secondary">{nutritionInfo.calorias} kcal</Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText primary="Proteínas" />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" color="text.secondary">{nutritionInfo.proteinas} g</Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText primary="Carbohidratos" />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" color="text.secondary">{nutritionInfo.carbohidratos} g</Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                 <ListItem>
                  <ListItemText primary="Grasas" />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" color="text.secondary">{nutritionInfo.grasas} g</Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                 <ListItem>
                  <ListItemText primary="Fibra" />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" color="text.secondary">{nutritionInfo.fibra} g</Typography>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Box>
          )}

          {/* Botón para mostrar análisis completo de la IA */}
          {!loadingImageAnalysis && respuestaIA && (
            <Box mt={2}>
              <Button
                variant="text"
                endIcon={<ExpandMoreIcon sx={{ transform: showAnalysis ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                onClick={() => setShowAnalysis(v => !v)}
                sx={{ color: 'primary.main', fontWeight: 600 }}
              >
                {showAnalysis ? 'Ocultar análisis completo' : 'Ver análisis completo'}
              </Button>
              <Collapse in={showAnalysis}>
                <Box mt={1} p={2} sx={{ bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#495057', lineHeight: 1.6 }}>
                    {respuestaIA}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          )}
        </Paper>
      )}

      {showValidation && validationResult && (
        <Paper elevation={3} sx={{ p: 2, mb: 2, maxWidth: 350, border: '2px solid #ff9800', borderRadius: 4 }}>
          <Typography variant="subtitle1" fontWeight={700} color={validationResult.validez === 'válido' ? 'success.main' : 'warning.main'} mb={1}>
            {validationResult.validez === 'válido' ? 'Validación exitosa' : '¡Revisa tu selección!'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>{validationResult.comentario}</Typography>
          <Typography variant="body2" color="primary.main" mb={1}><b>Ingredientes finales:</b> {validationResult.ingredientes_finales?.join(', ')}</Typography>
          <Typography variant="body2" color="primary.main" mb={1}><b>Nutrición recalculada:</b> {Object.entries(validationResult.nutricion || {}).map(([k,v]) => `${k}: ${v}`).join(', ')}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#388e3c', mt: 1 }}>{validationResult.analisis}</Typography>
          <Stack direction="row" spacing={2} mt={2}>
            <Button variant="contained" color="primary" onClick={async () => { setShowValidation(false); await handleAddToDay(); }}>Confirmar y guardar</Button>
            <Button variant="outlined" color="secondary" onClick={() => setShowValidation(false)}>Editar ingredientes</Button>
          </Stack>
        </Paper>
      )}

      {/* Botón principal */}
      {image && !loadingImageAnalysis && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ borderRadius: 3, fontWeight: 700, fontSize: '1.1rem', maxWidth: 350, mb: 2 }}
          onClick={handleAddToDayWithValidation}
          disabled={selected.length === 0}
        >
          Agregar a mi día
        </Button>
      )}

      {/* Botón secundario para volver a la pantalla inicial */}
      {image && !loadingImageAnalysis && (
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          sx={{ borderRadius: 3, fontWeight: 600, maxWidth: 350, mb: 2 }}
          onClick={handleReset}
        >
          Toma una foto o sube tu plato
        </Button>
      )}

      <FeedbackModal
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        title="Green Balance"
        message={`¡Vegetales agregados a tu día:
${detected.map(capitalizeFirstLetter).join(', ')}!`}
        buttonText="Ir a mi resumen"
        type="success"
      />

      <FeedbackModal
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title="¡Ha ocurrido un error!"
        message={errorModalMessage}
        buttonText="Cerrar"
        type="error"
      />

      {/* Modal para imagen ampliada */}
      <ImageAnalysisModal open={showFullImage} fullscreen imageUrl={image || undefined} onAccept={() => setShowFullImage(false)} />

      {/* Modal de análisis inicial tras subir la foto */}
      <ImageAnalysisModal
        open={showInitialAnalysisModal}
        fullscreen={false}
        loading={loadingImageAnalysis}
        imageUrl={initialAnalysisData?.imageUrl || undefined}
        analysis={initialAnalysisData?.analysis}
        nutrition={initialAnalysisData?.nutrition}
        onAccept={() => setShowInitialAnalysisModal(false)}
      />

      {/* Modal de análisis a pantalla completa tras reanálisis */}
      <ImageAnalysisModal
        open={showAnalysisModal}
        fullscreen
        loading={loadingValidation}
        imageUrl={analysisModalData?.imageUrl || image || undefined}
        analysis={analysisModalData?.analisis}
        nutrition={analysisModalData?.nutricion}
        onAccept={handleAcceptReanalysis}
        onEdit={() => setShowAnalysisModal(false)}
        validez={analysisModalData?.validez}
        comentario={analysisModalData?.comentario}
        ingredientesFinales={analysisModalData?.ingredientes_finales}
      />
    </Box>
  );
} 